// ============= headerAndFooter.js =============
import autoTable from "jspdf-autotable";
import { DateisValid } from "../../../utils/commonComponent/errorAlert";
import { config, customFontFamily, waterMarkImgConfig } from "./certificateConfig";
import { certificateConfigData } from "./printCertifcate";

// Paths for header/footer images
const Header = "/image/Nabl_Header.png";
const Footer = "/image/footer.png";
const LogoImage = "/image/WatermarkLogo.png";
const ScanCopyImage = "/image/scancopy.png";
const DraftImage = "/image/draft.png";

// local Y tracker
let yPositionGlobal = 0;

// Getters & setters
export const getHeaderHeight = () =>
  config.borderMargin +
  config.headerImageHeight +
  config.testReportTitleHeight +
  config.topInfoTableHeight +
  config.headerSpacing;

export const getFooterHeight = () =>
  config.footerMargin + config.footerImageHeight + config.certificateFooterHeight;

export const getYPositionGlobal = () => yPositionGlobal;
export const setYPositionGlobal = (newY) => (yPositionGlobal = newY);

// Draw text utility
export const drawText = (doc, text, x, y, options = {}) => {
  const { align = "left", bold = false, fontSize = config.fontSize } = options;
  doc.setFont(
  bold ? customFontFamily?.boldFont : customFontFamily?.normalFont,
  bold ? customFontFamily?.bold : customFontFamily?.normal
  );
  doc.setFontSize(fontSize);
  doc.text(text, x, y, { align });
};

// Draw border between header and footer
export const drawBorder = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const topY = getHeaderHeight();
  const bottomMargin = getFooterHeight() - config.certificateFooterSpacing;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(
    config.margin,
    topY,
    pageWidth - config.margin * 2,
    pageHeight - topY - bottomMargin
  );
};

// Draw header image
export const drawHeaderImage = (doc, letterHead) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const imgWidth = pageWidth - config.margin * 2;
  const imgHeight = config.headerImageHeight;
  const x = config.margin;
  const y = config.borderMargin;
  if (!letterHead) return;
  doc.addImage(Header, "PNG", x, y, imgWidth, imgHeight);
};

// Draw title
export const drawTestReportTitle = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const titleY =
    config.borderMargin + config.headerImageHeight + config.testReportTitleHeight / 2 + 5;
  drawText(doc, "TEST REPORT", pageWidth / 2, titleY, {
    align: "center",
    bold: true,
    fontSize: config.fontSize + 4,
  });
};

// Draw top info table
export const drawHeaderTable = (doc, cert) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const totalPageWidth = pageWidth - config.margin * 2;
  const startY =
    config.borderMargin + config.headerImageHeight + config.testReportTitleHeight;

  const topInfoData = [
    [
      { content: "ULR No. :", styles: { font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold } },
      { content: cert?.certificate_ulrNumber || "" },
      { content: "Date :", styles: { font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold } },
      { content: DateisValid(cert?.inward_inwardDate) },
    ],
    [
      { content: "Report No :", styles: { font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold } },
      { content: cert?.certificate_reportNumber || "" },
      { content: "Lab Reference No :", styles: { font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold } },
      { content: cert?.inward_labReferenceNo || "" },
    ],
  ];

  autoTable(doc, {
    startY,
    body: topInfoData,
    theme: "grid",
    styles: {
      fontSize: config.fontSize - 1,
      cellPadding: 3,
      halign: "left",
      valign: "middle",
      textColor: 0,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      font: customFontFamily?.normalFont,
      fontStyle: customFontFamily?.normal,
    },
    columnStyles: {
      0: { cellWidth: totalPageWidth * 0.2 },
      1: { cellWidth: totalPageWidth * 0.3 },
      2: { cellWidth: totalPageWidth * 0.2 },
      3: { cellWidth: totalPageWidth * 0.3 },
    },
    margin: { left: config.margin, right: config.margin },
  });
};

// Draw certificate footer section
export const drawFooterTable = (doc, cert) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - config.margin * 2;
  const footerData = cert?.certificate_footer || {};
  if (!footerData || Object.keys(footerData).length === 0) return;

  const startY = pageHeight - config.footerImageHeight - config.certificateFooterHeight;
  const entries = Object.entries(footerData);
  const colCount = entries.length;
  const colWidth = usableWidth / colCount;
  const imgHeight = 30;

  entries.forEach(([key, value], index) => {
    const x = config.margin + index * colWidth;
    let y = startY + 5;

    if (value?.usersign) {
      try {
        doc.addImage(value.usersign, "PNG", x + 10, y, colWidth - 20, imgHeight);
      } catch {
        console.warn("Invalid signature image:", value.usersign);
      }
    }

    y += imgHeight + 12;

    drawText(doc, `(${value?.userName || "-"} - ${value?.hrDesignation || "-"})`, x + colWidth / 2, y, {
      align: "center",
      bold: true,
      fontSize: 9,
    });

    y += 12;
    drawText(doc, value?.label || "", x + colWidth / 2, y, {
      align: "center",
      bold: true,
      fontSize: 9,
    });
  });
};

// Draw obs sheet footer: Tested By (obs-specific) + Reviewed By + Authorized By (NO Prepared By)
export const drawObsFooterTable = (doc, cert, obs, allSettings = []) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - config.margin * 2;
  const footerData = cert?.certificate_footer || {};

  const startY = pageHeight - config.footerImageHeight - config.certificateFooterHeight;
  const imgHeight = 30;

  // Build 3-column footer: Tested By | Reviewed By | Authorized By
  const allRoles = [
  {
    settingKey: "activeRole1",
    key: "testedBy",
    label: footerData?.testedBy?.label || "Tested By",
    // Use the correct field names that now exist on obs
    userName: obs?.testedBy_userName || "-",
    hrDesignation: obs?.testedBy_hrDesignation || "-",
    usersign: obs?.testedBy_userSign || null,
  },
  {
    settingKey: "activeRole3",
    key: "reviewedBy",
    label: footerData?.reviewedBy?.label || "Reviewed By",
    userName: footerData?.reviewedBy?.userName || "-",
    hrDesignation: footerData?.reviewedBy?.hrDesignation || "-",
    usersign: footerData?.reviewedBy?.usersign || null,
  },
  {
    settingKey: "activeRole4",
    key: "authorizedBy",
    label: footerData?.authorizedBy?.label || "Authorized By",
    userName: footerData?.authorizedBy?.userName || "-",
    hrDesignation: footerData?.authorizedBy?.hrDesignation || "-",
    usersign: footerData?.authorizedBy?.usersign || null,
  },
];

  const obsFooterEntries = allRoles.filter((role) =>
    allSettings.some((s) => s.keyName === role.settingKey && Number(s.status) === 1)
  );
  const colCount = obsFooterEntries.length;

  const colWidth = usableWidth / allRoles.length; // ALWAYS fixed to 1/3 for textcentering

  const getXPositions = (count) => {
    if (count === 1) {
      // Single: Left only
      return [config.margin];
    } else if (count === 2) {
      // Two: Left edge | Right edge (each occupying 1/3 width block)
      return [
        config.margin,    // Left block starts at margin
        config.margin + usableWidth - colWidth,   // Right block starts at right edge minus 1/3
      ];
    } else {
      // Three: Left | Center | Right
      return [
        config.margin,
        config.margin + colWidth,
        config.margin + colWidth * 2,
      ];
    }
  };

  const xPositions = getXPositions(colCount);

  obsFooterEntries.forEach((value, index) => {
    const x = xPositions[index];
    let y = startY + 5;

    if (value?.usersign) {
      try {
        doc.addImage(value.usersign, "PNG", x + 10, y, colWidth - 20, imgHeight);
      } catch {
        console.warn("Invalid signature image:", value.usersign);
      }
    }

    y += imgHeight + 12;
    drawText(doc, `(${value?.userName || "-"} - ${value?.hrDesignation || "-"})`, x + colWidth / 2, y, {
      align: "center", bold: true, fontSize: 9,
    });
    y += 12;
    drawText(doc, value?.label || "", x + colWidth / 2, y, {
      align: "center", bold: true, fontSize: 9,
    });
  });
};

// Draw footer image
export const drawFooterImage = (doc, letterHead) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - config.footerImageHeight - config.footerMargin + 10;
  const imgWidth = pageWidth - config.margin * 2;
  const imgHeight = config.footerImageHeight;
  const x = config.margin;
  if (!letterHead) return;
  doc.addImage(Footer, "PNG", x, footerY, imgWidth, imgHeight);
};

// Draw drawWaterMarkImg
export const drawWaterMarkImg = (doc) => {
  try {
    const props = certificateConfigData?.certificateProps || {};
    const { letterHead, scanCopy, draft } = props;

    const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
    const { logoWidth: lw, logoHeight: lh } = waterMarkImgConfig;
    const dx = (pw - 450) / 2, dy = (ph - 500) / 2, lx = (pw - lw) / 2, ly = (ph - lh) / 2;

    const img =
      letterHead && scanCopy ? ScanCopyImage :
      draft ? DraftImage :
      letterHead ? LogoImage : null;

    if (!img) return;

    const isLarge = draft || (letterHead && scanCopy);
    const w = isLarge ? 450 : lw, h = isLarge ? 500 : lh;
    const x = isLarge ? dx : lx, y = isLarge ? dy : ly;

    doc.addImage(img, "PNG", x, y, w, h);
  } catch (e) {
    console.error("Error drawing watermark:", e);
  }
};


// Initialize header + footer layout on each page
export const initDefaultPage = (doc, cert, letterHead, obs = null, allSettings = []) => {
  drawHeaderImage(doc, letterHead);
  drawTestReportTitle(doc);
  drawHeaderTable(doc, cert);
  drawBorder(doc);
  drawWaterMarkImg(doc);
  // If obs sheet is passed → use obs footer, else use main cert footer
  if (obs) {
    drawObsFooterTable(doc, cert, obs, allSettings);
  }
  //  else {
  //   drawFooterTable(doc, cert);
  // }
  drawFooterImage(doc, letterHead);
};